import React, { useState } from 'react';
import axios from 'axios';
import { ArrowLeft, CheckCircle2, XCircle } from 'lucide-react';
import ReportStatusBadge from './ReportStatusBadge';

const formatDate = (iso) => {
  if (!iso) return '';
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
};

const SnapshotBlock = ({ report, t }) => {
  const s = report.target_snapshot || {};
  const tt = report.target_type;

  const Row = ({ label, value }) =>
    value == null || value === '' ? null : (
      <div className="flex flex-col gap-0.5 py-1.5 border-b border-gray-100 last:border-0">
        <span className="text-[11px] uppercase tracking-wide text-gray-400">{label}</span>
        <span className="text-sm text-gray-800 break-words">{String(value)}</span>
      </div>
    );

  return (
    <div data-testid={`admin-snapshot-${tt}`} className="bg-gray-50 rounded-lg p-3 border border-gray-200">
      <div className="text-xs font-semibold text-gray-500 mb-2">
        {t('targetSnapshot')} · {t(`target${tt.charAt(0).toUpperCase() + tt.slice(1)}`)}
      </div>
      {tt === 'user' && (
        <>
          <Row label={t('name')} value={s.name} />
          <Row label="@" value={s.nickname} />
          <Row label="ID" value={s.id} />
          <Row label={t('privacy')} value={s.profile_privacy} />
        </>
      )}
      {tt === 'post' && (
        <>
          <Row label={t('author')} value={s.user_name} />
          <Row label={t('caption')} value={s.caption} />
          <Row label="ID" value={s.id} />
          <Row label={t('media')} value={s.media_type} />
        </>
      )}
      {tt === 'comment' && (
        <>
          <Row label={t('author')} value={s.user_name} />
          <Row label={t('text')} value={s.text} />
          <Row label="ID" value={s.id} />
          <Row label={t('postId')} value={s.post_id} />
        </>
      )}
      {tt === 'message' && (
        <>
          <Row label={t('sender')} value={s.sender_name} />
          <Row label={t('text')} value={s.message} />
          <Row label="ID" value={s.id} />
          <Row label={t('conversationId')} value={s.conversation_id} />
        </>
      )}
    </div>
  );
};

const AdminReportDetail = ({ token, API, t, isRTL, report, onBack, onUpdated }) => {
  const [current, setCurrent] = useState(report);
  const [note, setNote] = useState(report.admin_note || '');
  const [busy, setBusy] = useState(null); // 'review' | 'dismiss'
  const [error, setError] = useState(null);

  const headers = { Authorization: `Bearer ${token}` };

  const act = async (kind) => {
    setBusy(kind);
    setError(null);
    try {
      const res = await axios.post(
        `${API}/admin/reports/${current.id}/${kind}`,
        { admin_note: note || null },
        { headers }
      );
      setCurrent(res.data);
      onUpdated && onUpdated(res.data);
    } catch (err) {
      setError(err?.response?.data?.detail || t('error'));
    } finally {
      setBusy(null);
    }
  };

  return (
    <div data-testid="admin-report-detail-screen" dir={isRTL ? 'rtl' : 'ltr'} className="min-h-screen bg-gray-50">
      <header className="sticky top-0 z-30 bg-white border-b border-gray-100">
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center justify-between">
          <button onClick={onBack} data-testid="admin-detail-back" aria-label={t('back')} className="flex items-center gap-1 text-purple-600 font-medium p-2 -ms-2 min-h-[40px]">
            <ArrowLeft aria-hidden="true" className={`w-5 h-5 ${isRTL ? 'rotate-180' : ''}`} />
            <span className="text-sm">{t('back')}</span>
          </button>
          <h1 className="text-base font-bold text-gray-900">{t('reportDetail')}</h1>
          <div className="w-12" />
        </div>
      </header>

      <div className="max-w-2xl mx-auto px-4 py-4 space-y-4">
        <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-xs uppercase tracking-wide text-gray-400">{t('status')}</span>
            <ReportStatusBadge status={current.status} t={t} />
          </div>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div>
              <p className="text-[11px] uppercase tracking-wide text-gray-400">{t('reportedBy')}</p>
              <p className="text-gray-800 font-medium">{current.reporter?.name || t('unknownUser')}</p>
              {current.reporter?.nickname && <p className="text-xs text-gray-500">@{current.reporter.nickname}</p>}
            </div>
            <div>
              <p className="text-[11px] uppercase tracking-wide text-gray-400">{t('reportedAt')}</p>
              <p className="text-gray-800">{formatDate(current.created_at)}</p>
            </div>
            <div>
              <p className="text-[11px] uppercase tracking-wide text-gray-400">{t('reason')}</p>
              <p className="text-gray-800 font-medium">{t(`reason_${current.reason}`) || current.reason}</p>
            </div>
            <div>
              <p className="text-[11px] uppercase tracking-wide text-gray-400">{t('targetType')}</p>
              <p className="text-gray-800 font-medium capitalize">{current.target_type}</p>
            </div>
          </div>
          {current.details && (
            <div>
              <p className="text-[11px] uppercase tracking-wide text-gray-400 mb-1">{t('details')}</p>
              <p className="text-sm text-gray-800 bg-gray-50 rounded-lg px-3 py-2 border border-gray-200">{current.details}</p>
            </div>
          )}
        </div>

        <SnapshotBlock report={current} t={t} />

        {(current.reviewed_at || current.dismissed_at || current.admin_note) && (
          <div data-testid="admin-stamp" className="bg-white rounded-xl border border-gray-200 p-4 text-sm space-y-1">
            {current.reviewed_at && (
              <p className="text-emerald-700">
                ✓ {t('markedReviewedAt')} {formatDate(current.reviewed_at)}
              </p>
            )}
            {current.dismissed_at && (
              <p className="text-gray-600">
                ✕ {t('dismissedAt')} {formatDate(current.dismissed_at)}
              </p>
            )}
            {current.admin_note && (
              <p className="text-gray-700"><span className="font-semibold">{t('adminNote')}:</span> {current.admin_note}</p>
            )}
          </div>
        )}

        {/* Admin actions */}
        <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-3">
          <label className="block">
            <span className="text-xs font-semibold text-gray-700">{t('adminNote')} <span className="text-gray-400 font-normal">({t('optional')})</span></span>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              maxLength={500}
              rows={2}
              data-testid="admin-note-input"
              placeholder={t('adminNotePlaceholder')}
              className="mt-1 w-full px-3 py-2 rounded-lg border border-gray-200 focus:outline-none focus:border-purple-400 text-sm"
            />
            <span className="text-[10px] text-gray-400">{note.length}/500</span>
          </label>

          {error && (
            <div data-testid="admin-action-error" className="text-sm text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">{String(error)}</div>
          )}

          <div className="flex flex-col sm:flex-row gap-2">
            <button
              onClick={() => act('review')}
              disabled={busy !== null}
              data-testid="admin-mark-reviewed"
              className="flex-1 inline-flex items-center justify-center gap-2 py-2.5 rounded-lg bg-emerald-600 text-white font-semibold hover:bg-emerald-700 disabled:opacity-50"
            >
              <CheckCircle2 className="w-4 h-4" />
              {busy === 'review' ? t('saving') : t('markReviewed')}
            </button>
            <button
              onClick={() => act('dismiss')}
              disabled={busy !== null}
              data-testid="admin-dismiss"
              className="flex-1 inline-flex items-center justify-center gap-2 py-2.5 rounded-lg bg-gray-700 text-white font-semibold hover:bg-gray-800 disabled:opacity-50"
            >
              <XCircle className="w-4 h-4" />
              {busy === 'dismiss' ? t('saving') : t('dismiss')}
            </button>
          </div>
          <p className="text-[11px] text-gray-400">{t('adminPreserveHint')}</p>
        </div>
      </div>
    </div>
  );
};

export default AdminReportDetail;
